import { useState, useEffect } from "react";
//@ts-ignore
import { Treebeard } from "react-treebeard";
import { RxCross2 } from "react-icons/rx";
import api from "../../api";
import { MdDelete } from "react-icons/md";

/* ---------- tweakables ---------- */
const SHOW_ATTRIBUTES = false; // true → show minOccurs/type… as leaf nodes
const DEFAULT_EXPAND_DEPTH = 2; // 0=root only, 1=root + 1 level, etc.
const PATH_TRIM = 0;

const stripComments = (xml: string) => xml.replace(/<!--[\s\S]*?-->/g, "");

/* ---------- DOM helpers ---------- */

type Elem = Element;

interface Indexes {
  types: Record<string, Elem>;
  groups: Record<string, Elem>;
  elements: Record<string, Elem>;
}
const buildIndexes = (schema: Elem): Indexes => {
  const idx: Indexes = { types: {}, groups: {}, elements: {} };
  const walker = document.createTreeWalker(schema, NodeFilter.SHOW_ELEMENT);
  do {
    const el = walker.currentNode as Elem;
    const name = el.getAttribute("name");
    if (!name) continue;
    switch (el.localName) {
      case "complexType":
      case "simpleType":
        idx.types[name] = el;
        break;
      case "group":
        idx.groups[name] = el;
        break;
      case "element":
        idx.elements[name] = el;
        break;
    }
  } while (walker.nextNode());
  return idx;
};

const pickParticle = (c?: Elem | null) =>
  (c &&
    (Array.from(c.children).find((x) =>
      ["sequence", "choice", "all"].includes(x.localName)
    ) as Elem | undefined)) ||
  null;

const getContentParticle = (ct: Elem): Elem | null => {
  const cc = Array.from(ct.children).find(
    (x) => x.localName === "complexContent"
  ) as Elem | undefined;
  if (!cc) return pickParticle(ct);

  const extOrRes = Array.from(cc.children).find(
    (x) => x.localName === "extension" || x.localName === "restriction"
  ) as Elem | undefined;
  return extOrRes ? pickParticle(extOrRes) : null;
};

const buildTreeNode = (el: Elem, ix: Indexes, path: string[]): any => {
  if (el.hasAttribute("ref") && ix.elements[el.getAttribute("ref")!]) {
    el = ix.elements[el.getAttribute("ref")!]!;
  }

  const name =
    el.getAttribute("name") ||
    el.getAttribute("ref") ||
    el.getAttribute("type") ||
    "unnamed";
  const nodePath = [...path, name];

  const attrLeaves = SHOW_ATTRIBUTES
    ? Array.from(el.attributes).map((a) => ({
        name: `${a.name} = "${a.value}"`,
        path: [...nodePath, a.name],
        leaf: true,
      }))
    : [];

  let kids: Elem[] = [];

  const inlineCT = Array.from(el.children).find(
    (c) => c.localName === "complexType"
  ) as Elem | undefined;
  if (inlineCT) {
    const p = pickParticle(inlineCT) || getContentParticle(inlineCT);
    if (p)
      kids = Array.from(p.children).filter(
        (c) => c.localName === "element" || c.localName === "group"
      ) as Elem[];
  } else if (el.hasAttribute("type")) {
    const t = ix.types[el.getAttribute("type")!];
    if (t) {
      const p = pickParticle(t) || getContentParticle(t);
      if (p)
        kids = Array.from(p.children).filter(
          (c) => c.localName === "element" || c.localName === "group"
        ) as Elem[];
    }
  } else if (el.hasAttribute("group")) {
    const g = ix.groups[el.getAttribute("group")!];
    if (g) {
      const p = pickParticle(g);
      if (p)
        kids = Array.from(p.children).filter(
          (c) => c.localName === "element"
        ) as Elem[];
    }
  }

  const children = [
    ...attrLeaves,
    ...kids.map((c) => buildTreeNode(c, ix, nodePath)),
  ];

  return {
    name,
    path: nodePath,
    toggled: nodePath.length <= DEFAULT_EXPAND_DEPTH,
    children: children.length ? children : undefined,
  };
};

// const schemaToTree = (schema: Elem, fname: string) => {
//   const ix = buildIndexes(schema);
//   const tops = Array.from(schema.children).filter(
//     (c) => c.localName === "element"
//   ) as Elem[];
//   return {
//     name: fname,
//     toggled: true,
//     path: [fname],
//     children: tops.map((t) => buildTreeNode(t, ix, [fname])),
//   };
// };
const schemaToTree = (schema: Elem, fname: string) => {
  const ix = buildIndexes(schema);

  /* find the top-level xs:element nodes */
  const topElems = Array.from(schema.children).filter(
    (c) => c.localName === "element"
  ) as Elem[];

  /* ⬇️ NEW: make the first <element> the root node */
  return buildTreeNode(topElems[0], ix, []);
  /* (If your schema had multiple independent top-level elements,
     pick a different one or wrap them again.) */
};

export default function XSDExplorer({
  showXSDExplorer,
  xsdExplorerActive,
  setSelectedXsdPath,
  selectedXsdPath,
  xsdPathConditions,
  setXsdPathConditions,
  selectedXsdId,
  setSelectedXsdId,
  xsdXml,
  setXsdXml,
  treeData,
  setTreeData,
}: any) {
  const [xsdList, setXsdList] = useState<
    { xsd_id: string; xsd_name: string }[]
  >([]);

  const [cursor, setCursor] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCondition, setSelectedCondition] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [logicKey, setLogicKey] = useState("AND");

  // Fetch all XSDs on mount
  useEffect(() => {
    if (!xsdExplorerActive) return;
    setLoading(true);
    setError(null);
    api
      .get(process.env.REACT_APP_ALL_XSD!)
      .then((res) => {
        setXsdList(res.data);
      })
      .catch((err) => setError("Failed to fetch XSD list."))
      .finally(() => setLoading(false));
  }, [xsdExplorerActive]);

  // Fetch selected XSD XML when selectedXsdId changes
  useEffect(() => {
    if (!selectedXsdId) return;
    setLoading(true);
    setError(null);
    api
      .get(`${process.env.REACT_APP_XSD_DATA}${selectedXsdId}`)
      .then((res) => {
        setXsdXml(res.data?.XSD_Content || res.data);
      })
      .catch((err) => setError("Failed to fetch XSD file."))
      .finally(() => setLoading(false));
  }, [selectedXsdId]);

  // Parse and display tree when xsdXml changes
  useEffect(() => {
    if (!xsdXml) return;
    try {
      const doc = new DOMParser().parseFromString(
        stripComments(xsdXml),
        "application/xml"
      );
      setTreeData(
        schemaToTree(
          doc.documentElement,
          xsdList.find((x) => x.xsd_id === selectedXsdId)?.xsd_name || "XSD"
        )
      );
      setCursor(null);
      setSelectedXsdPath(null);
    } catch (e) {
      setError("Failed to parse XSD XML.");
    }
  }, [xsdXml, selectedXsdId, xsdList, setSelectedXsdPath]);

  const onToggle = (node: any, toggled: boolean) => {
    if (cursor) cursor.active = false;
    node.active = true;
    if (node.children) node.toggled = toggled;
    setCursor(node);
    setSelectedXsdPath(node.path.slice(PATH_TRIM).join("/"));
    setTreeData({ ...treeData });
  };

  if (!xsdExplorerActive) return null;

  return (
    <div
      onClick={showXSDExplorer}
      className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-md"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#EDEAF8] rounded-lg shadow-2xl w-[90vw] max-w-2xl max-h-[80vh] overflow-auto relative"
      >
        <button
          onClick={showXSDExplorer}
          className="absolute top-2 right-2 text-[25px] bg-[#4D30B5] hover:bg-[#1b1436] duration-300 rounded-lg p-1 text-white"
        >
          <RxCross2 size={25} />
        </button>

        <div className="p-6 space-y-4">
          <h1 className="text-center text-2xl font-bold">XSD Explorer</h1>

          <div>
            <label className="block mb-1 font-semibold">Select XSD</label>
            {loading && <div>Loading...</div>}
            {error && <div className="text-red-600">{error}</div>}
            <select
              value={selectedXsdId}
              onChange={(e) => setSelectedXsdId(e.target.value)}
              className="border-2 text-black outline-none p-2 rounded-lg w-full mb-2"
              disabled={loading || xsdList.length === 0}
            >
              <option value="" disabled>
                Select XSD
              </option>
              {xsdList.map((xsd) => (
                <option key={xsd.xsd_id} value={xsd.xsd_id}>
                  {xsd.xsd_name}
                </option>
              ))}
            </select>
          </div>

          <div className="h-[300px] overflow-auto border rounded bg-white p-2">
            {treeData && (
              <Treebeard
                data={treeData}
                onToggle={onToggle}
                style={{
                  tree: {
                    base: {
                      listStyle: "none",
                      margin: 0,
                      padding: 0,
                      color: "#333",
                      background: "transparent",
                      fontSize: "0.95rem",
                    },
                    node: {
                      activeLink: { background: "#b5b1c4", color: "#222" },
                    },
                  },
                }}
              />
            )}
          </div>

          <div>
            <strong>Selected path:&nbsp;</strong>
            <span className="text-blue-700 break-all">
              {selectedXsdPath || "—"}
            </span>
          </div>

          <div className="flex items-center mt-4">
            <select
              value={selectedCondition}
              onChange={(e) => setSelectedCondition(e.target.value)}
              className="border-2 text-black outline-none p-2 rounded-lg mr-2"
            >
              <option value="" disabled>
                Select Condition
              </option>
              <option value="Contains">Contains</option>
              <option value="Starts With">Starts With</option>
              <option value="Ends With">Ends With</option>
              <option value="Equal">Equal</option>
              <option value="Not Contains">Not Contains</option>
              <option value="Not Starts With">Not Starts With</option>
              <option value="Not Ends With">Not Ends With</option>
              <option value="Not Equal">Not Equal</option>
            </select>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Enter value"
              className="border-2 text-black outline-none p-2 rounded-lg mr-2"
            />
            {/* Render logic key dropdown if there are already conditions */}
            {xsdPathConditions.length > 0 && (
              <select
                value={logicKey}
                onChange={(e) => setLogicKey(e.target.value)}
                className="border-2 text-black outline-none p-2 rounded-lg mr-2"
              >
                <option value="AND">AND</option>
                <option value="OR">OR</option>
              </select>
            )}
            <button
              onClick={() => {
                if (selectedXsdPath && selectedCondition && inputValue) {
                  // Find the selected XSD name based on the selectedXsdId
                  const selectedXsd = xsdList.find(
                    (xsd) =>
                      String(xsd.xsd_id).trim() === String(selectedXsdId).trim()
                  );
                  const xsdName = selectedXsd ? selectedXsd.xsd_name : ""; // Get the name or default to an empty string

                  setXsdPathConditions((prev: any) => [
                    ...prev,
                    {
                      xsdName: xsdName,
                      path: selectedXsdPath,
                      condition: selectedCondition,
                      value: inputValue,
                      logic: prev.length > 0 ? logicKey : undefined,
                    },
                  ]);
                  setSelectedCondition("");
                  setInputValue("");
                  setLogicKey("AND");
                }
              }}
              className="bg-blue-500 text-white p-2 rounded-lg"
            >
              Add Path
            </button>
          </div>

          {/* Display added conditions */}
          <div className="mt-4">
            <h2 className="font-bold">Added Conditions:</h2>
            <ul className="flex flex-col gap-2">
              {xsdPathConditions.map((item: any, index: any) => (
                <li
                  key={index}
                  className="border border-gray-300 p-2 rounded-lg flex justify-between items-center"
                >
                  <span>
                    {item.xsdName} - {item.path} - {item.condition} -
                    {item.value}
                    {item.logic ? `-${item.logic}` : ""}
                  </span>
                  {index !== 0 || xsdPathConditions.length === 1 ? (
                    <button
                      onClick={() => {
                        if (index === 0) {
                          if (xsdPathConditions.length === 1) {
                            setXsdPathConditions([]);
                          }
                        } else {
                          setXsdPathConditions((prev: any) =>
                            prev.filter((_: any, i: any) => i !== index)
                          );
                        }
                      }}
                      className="bg-red-500 hover:bg-red-700 duration-300 text-white p-1 rounded-lg ml-2"
                    >
                      <MdDelete />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
